import { useCallback } from 'react';
import { useFormikContext } from 'formik';
import { useAppDispatch } from '@reduxStore';
import { queueModal } from '@components/Modals/modalSlice';
import { Vessel, Voyage, VoyageWithVesselInfo } from '@api/Trinium/Endpoints/Vessels/types';

interface UseVesselVoyageModalArgs {
	isExport: boolean;
	/** Formik field holding the Vessel. Updated with whatever vessel the modal saved against. */
	vesselFieldName?: string;
	/** Formik field holding the Voyage. Updated with the saved voyage. */
	voyageFieldName?: string;
	/** Runs after the saved voyage is applied, so the caller can sync dependent fields (e.g. FRD / Cutoff) */
	onVoyageApplied?: (voyage: VoyageWithVesselInfo | null) => void;
}

interface OpenVesselVoyageModalArgs {
	/** Vessel to seed the modal with, if the calling form already has one */
	vessel?: Vessel | null;
	/** Voyage to open for editing. Omit to add a new one. */
	voyageId?: number;
	/** Optional gate so a save can be ignored, e.g. editing a voyage other than the selected one */
	shouldApply?: (savedVoyage: Voyage) => boolean;
}

/**
 * Opens the combined Vessel & Voyage modal and writes whatever it saves back onto the calling form.
 * Both the Vessel and the Voyage inputs use this, so either entry point ends up with the pair in sync.
 */
export default function useVesselVoyageModal({
	isExport,
	vesselFieldName,
	voyageFieldName,
	onVoyageApplied
}: UseVesselVoyageModalArgs) {
	const appDispatch = useAppDispatch();
	const { setFieldValue } = useFormikContext();

	return useCallback(
		({ vessel, voyageId, shouldApply }: OpenVesselVoyageModalArgs) => {
			appDispatch(
				queueModal({
					Component: 'vesselVoyage',
					Props: {
						vessel,
						voyageId,
						isExport,
						onSaved: (savedVessel: Vessel, savedVoyage: Voyage) => {
							if (shouldApply && !shouldApply(savedVoyage)) return;

							const enriched: VoyageWithVesselInfo = {
								...savedVoyage,
								vesselId: savedVessel.id,
								isExport
							};

							if (vesselFieldName) void setFieldValue(vesselFieldName, savedVessel, true);
							if (voyageFieldName) void setFieldValue(voyageFieldName, enriched, true);

							onVoyageApplied?.(enriched);
						}
					}
				})
			);
		},
		[appDispatch, isExport, onVoyageApplied, setFieldValue, vesselFieldName, voyageFieldName]
	);
}
